import { useCallback } from 'react';
import { DropTargetMonitor } from 'react-dnd';
import {
  hash,
  emptyId,
  isSameId,
  useEditorStore,
  useFlowBuilder2Store,
  useFlowBuilder2MoveLayoutComponentMutation,
  useFlowBuilder2AddLayoutComponentsMutation,
  useFlowBuilder2UpdateLayoutComponentMutation,
  useFlowBuilder2DeleteLayoutComponentMutation,
} from '@diana/core';
import {
  IDndLayoutComponentDragObject as IDragObject,
  IDndLayoutComponentDropResult as IDropResult,
} from '../../../../../providers/DndLayoutComponents2';
import { useOpenComponent } from '../../../Support/useOpenComponent';
import { useSyncComponentNameAndDataPath } from '../../../Support/useSyncComponentNameAndDataPath';

interface IUseDndComponentDropEventProps {
  layoutId: string;
  taskId: string | null;
  stepId: string | null;
  flowId: string | null;
}

interface IDndComponentDropEventData {
  dragObject: IDragObject;
  targetElement: HTMLElement | null;
  targetDropResult: IDropResult;
  monitor: DropTargetMonitor<IDragObject, IDropResult>;
}

export const useDndComponentDropEvent = (props: IUseDndComponentDropEventProps) => {
  const { layoutId, taskId, stepId, flowId } = props;

  const getLayoutById = useEditorStore((s) => s.getLayoutById);
  const setTemporaryLayoutComponentId = useEditorStore((s) => s.setTemporaryLayoutComponentId);
  const registerRequest = useFlowBuilder2Store((s) => s.registerRequest);

  const { mutateAsync: moveComponent } = useFlowBuilder2MoveLayoutComponentMutation({});
  const { mutateAsync: addComponents } = useFlowBuilder2AddLayoutComponentsMutation({});
  const { mutateAsync: updateComponent } = useFlowBuilder2UpdateLayoutComponentMutation({});
  const { mutateAsync: deleteComponent } = useFlowBuilder2DeleteLayoutComponentMutation({});

  const openComponent = useOpenComponent();

  const syncComponentNameAndDataPath = useSyncComponentNameAndDataPath({
    layoutId,
    taskId,
    stepId,
    flowId,
  });

  const onDndComponentDrop = useCallback(
    (args: IDndComponentDropEventData) => {
      const { dragObject, targetDropResult, monitor } = args;
      const dropResult = monitor.getDropResult() ?? targetDropResult;

      const origin = dragObject.origin;
      const destination = dropResult.location;

      const component = syncComponentNameAndDataPath({
        ...dragObject.component,
        parentId: destination.parentId,
      });
      const descendants = dragObject.descendants.map(syncComponentNameAndDataPath);

      const resetTemporaryStatus = () => {
        setTemporaryLayoutComponentId(dragObject.component.id, false);
        for (const descendant of dragObject.descendants) {
          setTemporaryLayoutComponentId(descendant.id, false);
        }
      };

      // no origin layout
      if (!origin) {
        const layout = getLayoutById(dropResult.location.layoutId);
        if (layout) {
          openComponent({ layoutId: layout.id, componentId: component.id });
          registerRequest(
            () =>
              addComponents({
                layoutId: layout.id,
                afterComponent: layout.components[destination.index - 1]?.id ?? emptyId,
                data: [component, ...descendants],
              }).then(resetTemporaryStatus),
            {
              key: hash(['DND_ADD_COMPONENT', layout.id, component.id]),
              objectId: layout.id,
              objectType: 'layout',
            },
          );
        }

        return dropResult;
      }

      // same layout
      if (origin.layoutId === destination.layoutId) {
        const layout = getLayoutById(destination.layoutId);
        if (layout) {
          (async () => {
            // move component
            await registerRequest(
              () =>
                moveComponent({
                  layoutId: destination.layoutId,
                  componentId: component.id,
                  afterComponentId: layout.components[destination.index - 1]?.id ?? emptyId,
                }).then(resetTemporaryStatus),
              {
                key: hash(['DND_MOVE_COMPONENT', component.id]),
                objectId: component.id,
                objectType: 'layoutComponent',
              },
            );

            // move descendants
            for (let i = 0; i < descendants.length; i++) {
              await registerRequest(
                () =>
                  moveComponent({
                    layoutId: destination.layoutId,
                    componentId: descendants[i].id,
                    afterComponentId: descendants[i - 1]?.id ?? component.id,
                  }),
                {
                  key: hash(['DND_MOVE_COMPONENT', descendants[i].id]),
                  objectId: descendants[i].id,
                  objectType: 'layoutComponent',
                },
              );
            }

            // update parent Id
            if (!isSameId(origin.parentId, destination.parentId)) {
              await registerRequest(
                () =>
                  updateComponent({
                    layoutId: destination.layoutId,
                    componentId: component.id,
                    original: component,
                    data: { parentId: destination.parentId },
                  }),
                {
                  key: hash(['DND_CHANGE_PARENT_ID', component.id]),
                  objectId: component.id,
                  objectType: 'layoutComponent',
                },
              );
            }
          })().catch();
        }
        return dropResult;
      }

      // different layout
      if (origin.layoutId !== destination.layoutId) {
        const originLayout = getLayoutById(origin.layoutId);
        const destinationLayout = getLayoutById(destination.layoutId);

        if (originLayout && destinationLayout) {
          (async () => {
            // add components to destination layout
            await registerRequest(
              () =>
                addComponents({
                  layoutId: destination.layoutId,
                  afterComponent:
                    destinationLayout.components[destination.index - 1]?.id ?? emptyId,
                  data: [component, ...descendants],
                }).then(resetTemporaryStatus),
              {
                key: hash(['DND_ADD_COMPONENT', destination.layoutId, component.id]),
                objectId: destination.layoutId,
                objectType: 'layout',
              },
            );

            // delete component from source layout
            const componentIdToDelete = dragObject.originalComponent.id;
            await registerRequest(
              () =>
                deleteComponent({ layoutId: origin.layoutId, componentId: componentIdToDelete }),
              {
                key: hash(['DND_DELETE_COMPONENT', componentIdToDelete]),
                objectId: componentIdToDelete,
                objectType: 'layoutComponent',
              },
            );
          })().catch();
        }
        return dropResult;
      }

      return dropResult;
    },
    [
      addComponents,
      deleteComponent,
      getLayoutById,
      moveComponent,
      openComponent,
      registerRequest,
      updateComponent,
      setTemporaryLayoutComponentId,
      syncComponentNameAndDataPath,
    ],
  );

  return { onDndComponentDrop };
};
